import { Link } from 'react-router-dom'
import type { SiteEvent } from '../api/public'
import { cmsImageSrcSet, cmsImageUrl } from '../lib/cms-image'
import { formatCrtDateDots } from '../lib/event-format'
import { handleCmsImageError } from '../lib/img-fallback'
import { EVENT_GALLERY_ANCHOR } from './EventGallery'

/** Photos shown as a teaser; the rest are counted in the overflow tile. */
const PREVIEW_COUNT = 4

const PREVIEW_WIDTHS = [160, 320, 480] as const
const PREVIEW_SIZES = '(min-width:900px) 140px, 22vw'

/**
 * "Rückblick" block: pins one past event to the home page so guests land on its
 * gallery in one click instead of digging through /events. Rendered only when
 * the CMS flag is set and the event has actually passed.
 */
export function HomeRecap({ ev }: { ev: SiteEvent }) {
  const photos = ev.galleryUrls ?? []
  const preview = photos.slice(0, PREVIEW_COUNT)
  const remaining = photos.length - preview.length
  const galleryPath = `/events/${ev.slug}${photos.length ? `#${EVENT_GALLERY_ANCHOR}` : ''}`
  const where = [ev.venueName, ev.locationLabel].filter(Boolean).join(' · ')
  const meta = [formatCrtDateDots(ev.startsAt), where].filter(Boolean).join(' · ')

  return (
    <section className="section recap" id="rueckblick" aria-labelledby="recap-title">
      <div className="wrap">
        <div className="recap__card">
          <div className="kicker crt">↳ /rückblick</div>

          <h2 id="recap-title" className="display h2 recap__title">
            <Link to={galleryPath}>{ev.title}</Link>
          </h2>

          <div className="recap__meta">{meta}</div>

          {preview.length > 0 && (
            <Link
              to={galleryPath}
              className="recap__strip"
              aria-label={`Galerie von ${ev.title} öffnen — ${photos.length} Fotos`}
            >
              {preview.map((url, i) => {
                const isLast = i === preview.length - 1 && remaining > 0
                return (
                  <span className="recap__shot" key={`${url}-${i}`}>
                    <img
                      src={cmsImageUrl(url, 'thumb') ?? url}
                      srcSet={cmsImageSrcSet(url, PREVIEW_WIDTHS)}
                      sizes={PREVIEW_SIZES}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      onError={(e) => handleCmsImageError(e, '.recap__shot')}
                    />
                    {isLast && (
                      <span className="recap__more" aria-hidden>
                        +{remaining}
                      </span>
                    )}
                  </span>
                )
              })}
            </Link>
          )}

          <div className="recap__actions">
            <Link className="btn btn-crt" to={galleryPath}>
              {photos.length ? 'Galerie ansehen →' : 'Zum Event →'}
            </Link>
            {photos.length > 0 && (
              <span className="recap__count muted">{photos.length} Fotos</span>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
